import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API, withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } });

http.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("welp_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// After a new deployment the backend revokes all sessions. When that happens,
// clear the stale token and hard-reload so the browser fetches the newest version.
http.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err?.config?.url || "";
    if (err?.response?.status === 401 && url.includes("/auth/me") && localStorage.getItem("welp_token")) {
      localStorage.removeItem("welp_token");
      window.location.replace("/login");
      return new Promise(() => {});
    }
    return Promise.reject(err);
  }
);

export const api = {
  captcha: () => http.post("/captcha").then(r => r.data),
  register: (b) => http.post("/auth/register", b).then(r => r.data),
  login: (b) => http.post("/auth/login", b).then(r => r.data),
  phoneStart: (phone) => http.post("/auth/phone/start", { phone }).then(r => r.data),
  phoneVerify: (code) => http.post("/auth/phone/verify", { code }).then(r => r.data),
  phoneResend: () => http.post("/auth/phone/resend").then(r => r.data),
  forgotPassword: (b) => http.post("/auth/forgot-password", b).then(r => r.data),
  resetPassword: (b) => http.post("/auth/reset-password", b).then(r => r.data),
  me: () => http.get("/auth/me").then(r => r.data),
  googleSession: (session_id) => http.post("/auth/google/session", { session_id }).then(r => r.data),
  logout: () => http.post("/auth/logout").then(r => r.data),
  platforms: () => http.get("/platforms").then(r => r.data),
  searchProfile: (username, platform) =>
    http.get("/profiles/search", { params: { username, platform } }).then(r => r.data),
  snapshot: (pid) => http.get(`/profiles/${pid}/snapshot`).then(r => r.data),
  skinny: (pid) => http.get(`/profiles/${pid}/skinny`).then(r => r.data),
  experiences: (pid) => http.get(`/profiles/${pid}/experiences`).then(r => r.data),
  submitReview: (b) => http.post("/reviews", b).then(r => r.data),
  submitResponse: (b) => http.post("/responses", b).then(r => r.data),
  claimProfile: (pid, body) => http.post(`/profiles/${pid}/claim`, body).then(r => r.data),
  disputeClaim: (pid) => http.post(`/profiles/${pid}/dispute`).then(r => r.data),
  myReviews: () => http.get("/users/me/reviews").then(r => r.data),
  uploadAvatar: (pid, image_b64) => http.post(`/profiles/${pid}/avatar`, { image_b64 }).then(r => r.data),
  getReview: (rid) => http.get(`/reviews/${rid}`).then(r => r.data),
  notifications: () => http.get("/notifications").then(r => r.data),
  markRead: (nid) => http.post(`/notifications/${nid}/read`).then(r => r.data),
  track: (path, referrer) => {
    let cid = localStorage.getItem("welp_cid");
    if (!cid) {
      cid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("welp_cid", cid);
    }
    return http.post("/track", { path, referrer, client_id: cid }).then(r => r.data).catch(() => null);
  },
  adminStats: (tz) => http.get("/admin/stats", { params: tz ? { tz } : {} }).then(r => r.data),
  adminSearchUsers: (q) => http.get("/admin/users", { params: { q } }).then(r => r.data),
  adminSendMessage: (b) => http.post("/admin/message", b).then(r => r.data),
  adminHealth: () => http.get("/admin/health").then(r => r.data),
  adminAnalyticsReset: () => http.post("/admin/analytics/reset").then(r => r.data),
  adminOverview: () => http.get("/admin/overview").then(r => r.data),
  evt: (event, meta = null) => {
    const cid = localStorage.getItem("welp_cid");
    return http.post("/events", { event, meta, client_id: cid }).then(r => r.data).catch(() => null);
  },
  adminContactMessages: () => http.get("/admin/contact-messages").then(r => r.data),
  adminContactRead: (id) => http.post(`/admin/contact-messages/${id}/read`).then(r => r.data),
  adminContactReply: (id, message) => http.post(`/admin/contact-messages/${id}/reply`, { message }).then(r => r.data),
  adminFeedback: () => http.get("/admin/feedback").then(r => r.data),
  adminFeedbackRead: (id) => http.post(`/admin/feedback/${id}/read`).then(r => r.data),
  adminFeedbackReply: (id, message) => http.post(`/admin/feedback/${id}/reply`, { message }).then(r => r.data),
  uploadMyAvatar: (image_b64) => http.post("/users/me/avatar", { image_b64 }).then(r => r.data),
  removeMyAvatar: () => http.delete("/users/me/avatar").then(r => r.data),
  tarot: (question, spread = 3, layout = null) => http.post("/zodiac/tarot", { question, spread, layout }).then(r => r.data),
  tarotManual: (question, cards, layout = null) => http.post("/zodiac/tarot/manual", { question, cards, layout }).then(r => r.data),
  tarotClarify: (reading_id) => http.post("/zodiac/tarot/clarify", { reading_id }).then(r => r.data),
  tarotSpreads: () => http.get("/zodiac/tarot/spreads").then(r => r.data),
  tarotStatus: () => http.get("/zodiac/tarot/status").then(r => r.data),
  tarotHistory: () => http.get("/zodiac/tarot/history").then(r => r.data),
  tarotCheckout: (lookup_key, spread, origin_url) => http.post("/payments/checkout", { lookup_key, spread, origin_url }).then(r => r.data),
  paymentStatus: (sessionId) => http.get(`/payments/status/${sessionId}`).then(r => r.data),
  reactToReview: (rid, reaction) => http.post(`/reviews/${rid}/react`, { reaction }).then(r => r.data),
  saveMyStory: (story, reflection) => http.post("/users/me/story", { story, reflection }).then(r => r.data),
  storyReactions: (pid) => http.get(`/profiles/${pid}/story/reactions`).then(r => r.data),
  reactToStory: (pid, reaction) => http.post(`/profiles/${pid}/story/react`, { reaction }).then(r => r.data),
  getMyStory: () => http.get("/users/me/story").then(r => r.data),
  createChangeRequest: (path, note) => http.post("/admin/change-requests", { path, note }).then(r => r.data),
  listChangeRequests: () => http.get("/admin/change-requests").then(r => r.data),
  completeChangeRequest: (id) => http.post(`/admin/change-requests/${id}/done`).then(r => r.data),
  submitFeedback: (b) => http.post("/feedback", b).then(r => r.data),
  submitContact: (b) => http.post("/contact", b).then(r => r.data),
  reportCategories: () => http.get("/reports/categories").then(r => r.data),
  submitReport: (b) => http.post("/reports", b).then(r => r.data),
  natalChart: (b) => http.post("/zodiac/natal-chart", b).then(r => r.data),
  natalJob: (id) => http.get(`/zodiac/natal-jobs/${id}`).then(r => r.data),
  synastry: (b) => http.post("/zodiac/synastry", b).then(r => r.data),
  shareSynastry: (readingId) => http.post(`/zodiac/synastry/${readingId}/share`).then(r => r.data),
  sharedSynastry: (shareId) => http.get(`/zodiac/synastry/shared/${shareId}`).then(r => r.data),
  placeSearch: (q) => http.get("/zodiac/place-search", { params: { q } }).then(r => r.data),
  myNatalChart: () => http.get("/zodiac/my-natal-chart").then(r => r.data),
  listSavedCharts: () => http.get("/zodiac/saved-charts").then(r => r.data),
  saveChart: (b) => http.post("/zodiac/saved-charts", b).then(r => r.data),
  deleteSavedChart: (id) => http.delete(`/zodiac/saved-charts/${id}`).then(r => r.data),
};
